export enum INJURY_BODY_ZONE {
  TESTA = 'head_neck',
  SPALLA_DX = 'shoulder_dx',
  SPALLA_SX = 'shoulder_sx',
  GOMITO_DX = 'elbow_dx',
  GOMITO_SX = 'elbow_sx',
  POLSO_DX = 'wrist_hand_dx',
  POLSO_SX = 'wrist_hand_sx',
  TORACE = 'chest_ribs',
  SCHIENA_ALTA = 'upper_back',
  SCHIENA_BASSA = 'lumbar',
  ANCA_DX = 'hip_groin_dx',
  ANCA_SX = 'hip_groin_sx',
  COSCIA_DX = 'quadricep_dx',
  COSCIA_SX = 'quadricep_sx',
  HAMSTRING_DX = 'hamstring_dx',
  HAMSTRING_SX = 'hamstring_sx',
  GINOCCHIO_DX = 'knee_dx',
  GINOCCHIO_SX = 'knee_sx',
  POLPACCIO_DX = 'calf_dx',
  POLPACCIO_SX = 'calf_sx',
  CAVIGLIA_DX = 'ankle_foot_dx',
  CAVIGLIA_SX = 'ankle_foot_sx',
}

export enum INJURY_ONSET {
  ACUTO = 'acuto',
  GRADUALE = 'graduale',
  RECIDIVA = 'recidiva',
}

export enum INJURY_TYPE_EXTENDED {
  MUSCOLARE = 'muscolare',
  TENDINEO = 'tendineo',
  LEGAMENTOSO = 'legamentoso',
  OSSEO_FRATTURA = 'osseo_frattura',
  CONTUSIONE = 'contusione',
  DISTORSIONE = 'distorsione',
  LACERAZIONE = 'lacerazione',
  OVERUSE = 'overuse',
  LOMBALGIA = 'lombalgia',
  CONCUSSIONE = 'concussione',
  ALTRO = 'altro',
}

export enum INJURY_SEVERITY {
  SLIGHT = 'slight',
  MINIMAL = 'minimal',
  MILD = 'mild',
  MODERATE = 'moderate',
  SEVERE = 'severe',
  CAREER_ENDING = 'career-ending',
  DA_VALUTARE = 'da-valutare',
}

// Valori allineati 1:1 con il backend (apps/backend/src/enums/injuries.ts).
// Le label utente ("Partita", "Extra-sportivo") restano nel form; qui contano i value
// che vengono inviati e validati da @IsEnum sul DTO — vedi bug b3e591 (mismatch enum FE↔BE).
export enum INJURY_CONTEXT {
  GARA = 'gara',
  ALLENAMENTO = 'allenamento',
  RISCALDAMENTO = 'riscaldamento',
  FUORI_CAMPO = 'fuori_campo',
}

export enum INJURY_MECHANISM {
  NON_CONTATTO_SPRINT = 'non_contatto_sprint',
  CAMBIO_DIREZIONE = 'cambio_direzione',
  CONTATTO = 'contatto',
  OVERUSE = 'overuse',
  ALTRO = 'altro',
}

// Abbreviazioni per il badge
export const INJURY_BODY_ZONE_ABBR: Record<INJURY_BODY_ZONE, string> = {
  [INJURY_BODY_ZONE.TESTA]: 'TES',
  [INJURY_BODY_ZONE.SPALLA_DX]: 'SP-D',
  [INJURY_BODY_ZONE.SPALLA_SX]: 'SP-S',
  [INJURY_BODY_ZONE.GOMITO_DX]: 'GO-D',
  [INJURY_BODY_ZONE.GOMITO_SX]: 'GO-S',
  [INJURY_BODY_ZONE.POLSO_DX]: 'PO-D',
  [INJURY_BODY_ZONE.POLSO_SX]: 'PO-S',
  [INJURY_BODY_ZONE.TORACE]: 'TOR',
  [INJURY_BODY_ZONE.SCHIENA_ALTA]: 'SC-A',
  [INJURY_BODY_ZONE.SCHIENA_BASSA]: 'SC-B',
  [INJURY_BODY_ZONE.ANCA_DX]: 'AN-D',
  [INJURY_BODY_ZONE.ANCA_SX]: 'AN-S',
  [INJURY_BODY_ZONE.COSCIA_DX]: 'CO-D',
  [INJURY_BODY_ZONE.COSCIA_SX]: 'CO-S',
  [INJURY_BODY_ZONE.HAMSTRING_DX]: 'HA-D',
  [INJURY_BODY_ZONE.HAMSTRING_SX]: 'HA-S',
  [INJURY_BODY_ZONE.GINOCCHIO_DX]: 'GN-D',
  [INJURY_BODY_ZONE.GINOCCHIO_SX]: 'GN-S',
  [INJURY_BODY_ZONE.POLPACCIO_DX]: 'PL-D',
  [INJURY_BODY_ZONE.POLPACCIO_SX]: 'PL-S',
  [INJURY_BODY_ZONE.CAVIGLIA_DX]: 'CA-D',
  [INJURY_BODY_ZONE.CAVIGLIA_SX]: 'CA-S',
};

// Metriche atleta per /injuries/stats/athlete/:id/:metric
export enum ATHLETE_INJURY_METRIC {
  TIMELINE = 'timeline',
  PAIN_NRS = 'pain-nrs',
  BODY_MAP = 'body-map',
  RECURRENCE_MAP = 'recurrence-map',
  LOAD_AT_INJURY = 'load-at-injury',
  RETURN_CONFIDENCE = 'return-confidence',
  DAYS_LOST_SEVERITY = 'days-lost-severity',
  TYPE_BREAKDOWN = 'type-breakdown',
  CONTEXT_BREAKDOWN = 'context-breakdown',
  MONTHLY_RATE = 'monthly-rate',
  RTP_PROGRESS = 'rtp-progress',
  REINJURY_RISK = 'reinjury-risk',
  MECHANISM_BREAKDOWN = 'mechanism-breakdown',
  ACWR_SERIES = 'acwr-series',
  SRPE_WEEKLY = 'srpe-weekly',
  READINESS_INDEX = 'readiness-index',
  HOOPER_SERIES = 'hooper-series',
  TONNAGE_SERIES = 'tonnage-series',
  EXPOSURE_HOURS = 'exposure-hours',
  CMJ_RSI_SERIES = 'cmj-rsi-series',
  HRV_SERIES = 'hrv-series',
  LOAD_PROGRESSION = 'load-progression',
  INSIGHTS = 'insights',
}

// Metriche team per /injuries/stats/team/:id/:metric
export enum TEAM_INJURY_METRIC {
  TIMELINE = 'timeline',
  BODY_MAP = 'body-map',
  TYPE_BREAKDOWN = 'type-breakdown',
  MECHANISM_BREAKDOWN = 'mechanism-breakdown',
  SEVERITY_BREAKDOWN = 'severity-breakdown',
  LEADERBOARD = 'leaderboard',
  RECURRENCE_BY_POSITION = 'recurrence-by-position',
  MONTHLY_RATE = 'monthly-rate',
  RETURN_CONFIDENCE_AVG = 'return-confidence-avg',
  RTP_STATUS = 'rtp-status',
  ACWR_SERIES = 'acwr-series',
  HOOPER_SERIES = 'hooper-series',
  READINESS_INDEX = 'readiness-index',
  CMJ_RSI_SERIES = 'cmj-rsi-series',
  TEAM_INSIGHTS = 'team-insights',
}

export const INJURY_BODY_ZONE_LABEL: Record<INJURY_BODY_ZONE, string> = {
  [INJURY_BODY_ZONE.TESTA]: 'Testa / Collo',
  [INJURY_BODY_ZONE.SPALLA_DX]: 'Spalla DX',
  [INJURY_BODY_ZONE.SPALLA_SX]: 'Spalla SX',
  [INJURY_BODY_ZONE.GOMITO_DX]: 'Gomito DX',
  [INJURY_BODY_ZONE.GOMITO_SX]: 'Gomito SX',
  [INJURY_BODY_ZONE.POLSO_DX]: 'Polso / Mano DX',
  [INJURY_BODY_ZONE.POLSO_SX]: 'Polso / Mano SX',
  [INJURY_BODY_ZONE.TORACE]: 'Torace / Costole',
  [INJURY_BODY_ZONE.SCHIENA_ALTA]: 'Schiena Alta',
  [INJURY_BODY_ZONE.SCHIENA_BASSA]: 'Schiena Bassa',
  [INJURY_BODY_ZONE.ANCA_DX]: 'Anca / Inguine DX',
  [INJURY_BODY_ZONE.ANCA_SX]: 'Anca / Inguine SX',
  [INJURY_BODY_ZONE.COSCIA_DX]: 'Quadricipite DX',
  [INJURY_BODY_ZONE.COSCIA_SX]: 'Quadricipite SX',
  [INJURY_BODY_ZONE.HAMSTRING_DX]: 'Hamstring DX',
  [INJURY_BODY_ZONE.HAMSTRING_SX]: 'Hamstring SX',
  [INJURY_BODY_ZONE.GINOCCHIO_DX]: 'Ginocchio DX',
  [INJURY_BODY_ZONE.GINOCCHIO_SX]: 'Ginocchio SX',
  [INJURY_BODY_ZONE.POLPACCIO_DX]: 'Polpaccio DX',
  [INJURY_BODY_ZONE.POLPACCIO_SX]: 'Polpaccio SX',
  [INJURY_BODY_ZONE.CAVIGLIA_DX]: 'Caviglia / Piede DX',
  [INJURY_BODY_ZONE.CAVIGLIA_SX]: 'Caviglia / Piede SX',
};
